import { useState } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  useGetAddressesByBusinessPartnerId,
  useGetSalesPersonByBusinessPartnerId,
  useGetOrdersByBusinessPartnerId,
  useGetContactEmployeesWithUserByBusinessPartnerId,
} from '@/api/business-partner/business-partner.hooks';
import type { BpListContext } from './bp-browser.component';
import { BpHeader } from './bp-header.component';
import type { BpDetailContext } from './bp-detail-routes.component';

const PAGE_SIZE = 50;

/** Detail tabs — `seg` is the route path segment ('' = overview index route). */
const TABS: { id: 'overview' | 'orders' | 'contacts'; label: string; seg: string }[] = [
  { id: 'overview', label: 'Übersicht', seg: '' },
  { id: 'orders', label: 'Aufträge', seg: 'auftraege' },
  { id: 'contacts', label: 'Kontakte', seg: 'kontakte' },
];

/**
 * Detail panel for the partner addressed by the `:bpId` route param. Renders
 * the header + tabs and provides the per-tab data to its child routes.
 */
export function BpDetail() {
  const { bpId } = useParams<{ bpId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { partners, partnersPending } = useOutletContext<BpListContext>();

  const id = Number(bpId);
  const bp = partners.find((p) => p.id === id);

  // Server-paged orders; reset to the first page when the partner changes.
  const [ordersPage, setOrdersPage] = useState(0);
  const [syncedBpId, setSyncedBpId] = useState(bpId);
  if (syncedBpId !== bpId) {
    setSyncedBpId(bpId);
    setOrdersPage(0);
  }

  const { data: addresses = [] } = useGetAddressesByBusinessPartnerId(id, {
    enabled: !!id,
  });

  const { data: salesPerson } = useGetSalesPersonByBusinessPartnerId(id);

  const { data: ordersData, isPending: ordersPending } = useGetOrdersByBusinessPartnerId(
    id,
    PAGE_SIZE,
    ordersPage * PAGE_SIZE,
  );

  const { data: contacts = [], isPending: contactsPending } =
    useGetContactEmployeesWithUserByBusinessPartnerId(id, { enabled: !!id });

  const orders = ordersData?.data ?? [];
  const ordersTotal = ordersData?.total ?? 0;

  if (!bp) {
    return (
      <div className="bp-empty">
        <div>
          <div className="big">{partnersPending ? 'Laden…' : 'Partner nicht gefunden'}</div>
          {!partnersPending && <div>Wähle einen Partner aus der Liste.</div>}
        </div>
      </div>
    );
  }

  const activeSeg = pathname.includes('/kontakte')
    ? 'kontakte'
    : pathname.includes('/auftraege')
      ? 'auftraege'
      : '';

  const tabCount: Record<string, number | undefined> = {
    overview: undefined,
    orders: ordersTotal,
    contacts: contacts.length,
  };

  const context: BpDetailContext = {
    bp,
    addresses,
    salesPerson,
    orders,
    ordersTotal,
    ordersPending,
    ordersPage,
    setOrdersPage,
    contacts,
    contactsPending,
  };

  return (
    <div className="bp-detail-panel">
      <BpHeader bp={bp} addresses={addresses} ordersTotal={ordersTotal} />

      <div className="bp-tabs">
        {TABS.map((tab) => {
          const count = tabCount[tab.id];
          return (
            <button
              key={tab.id}
              className={`bp-tab${activeSeg === tab.seg ? ' active' : ''}`}
              onClick={() =>
                navigate(`/i/business-partners/${bpId}${tab.seg ? `/${tab.seg}` : ''}`)
              }
            >
              {tab.label}
              {count != null && count > 0 && <span className="tab-count">{count}</span>}
            </button>
          );
        })}
      </div>

      <Outlet context={context} />
    </div>
  );
}
